import {Link} from 'react-router-dom';
import clsx from 'clsx';
import {ReactNode} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {SITE_LOGO_ALT} from '@common/branding/site-logo';
import {Trans} from '@common/i18n/trans';
export const AUTH_PAGE_BG = '#F5F5F7';
export const AUTH_SIDEBAR_BG = '#E9ECF0';

import {
  authFieldBaseClass,
  authFieldClass,
  authFieldHalfClass,
  authActionOutsetClass,
} from './auth-tokens';

export {authFieldBaseClass, authFieldClass, authFieldHalfClass};

export const authPrimaryBtnClass =
  'flex h-[60px] w-[141px] items-center justify-center rounded-[24px] bg-[#2B7E20] text-[17px] font-medium leading-normal text-white transition-opacity hover:opacity-90 disabled:opacity-50';

export const authOutlineBtnClass =
  'flex h-[60px] w-[141px] items-center justify-center rounded-[24px] border-2 border-[#2B7E20] bg-white text-[17px] font-medium leading-normal text-[#535353] transition-colors hover:bg-[#F5F5F7] dark:bg-[#232832] dark:text-[#f3f4f6] dark:hover:bg-[#2f3440]';

interface AuthShellProps {
  logoSrc: string;
  children: ReactNode;
}

/** Original auth page shell: sidebar logo + centered form column. */
export function AuthShell({logoSrc, children}: AuthShellProps) {
  return (
    <div className="flex min-h-screen flex-wrap items-stretch bg-[#F5F5F7] transition-colors duration-300 dark:bg-[#14161a] md:flex-nowrap">
      <div
        className={clsx(
          'sidebar w-[281px] flex-none min-h-full max-[810px]:hidden transition-colors duration-300 md:flex-none',
          'bg-[#E9ECF0] dark:bg-[#1d2128]'
        )}
      >
        <Link aria-label="Go to homepage" to="/">
          <img
            className="logo-image mt-[54px] ml-[42px] w-[198px]"
            data-logo="navbar"
            src={logoSrc}
            alt={SITE_LOGO_ALT}
          />
        </Link>
      </div>

      <div className="main-content relative flex min-w-[300px] w-full max-w-[1639px] flex-1 justify-center bg-[#F5F5F7] transition-colors duration-300 dark:bg-[#14161a]">
        <div className="single-content flex w-full max-w-[520px] flex-col items-center pb-[6vh] pt-[17vh] max-[810px]:max-w-full">
          <Link
            aria-label="Go to homepage"
            to="/"
            className="mb-[24px] min-[811px]:hidden"
          >
            <img
              className="logo-image mt-[54px] ml-[42px] w-[198px]"
              src={logoSrc}
              alt={SITE_LOGO_ALT}
            />
          </Link>
          {children}
        </div>
      </div>
    </div>
  );
}

/** Wraps form card; optional close/back sits just outside the card top-right corner. */
export function AuthCardWrap({
  children,
  action,
  compact,
}: {
  children: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className={clsx(
        'main-content-1 relative w-full min-w-0',
        compact ? 'max-w-[402px]' : 'max-w-[520px]'
      )}
    >
      {action ? (
        <div className={clsx(authActionOutsetClass, 'z-20')}>{action}</div>
      ) : null}
      {children}
    </div>
  );
}

interface AuthCardProps {
  children: ReactNode;
  compact?: boolean;
}

export function AuthCard({children, compact}: AuthCardProps) {
  return (
    <div
      className={clsx(
        'flex w-full flex-col items-center rounded-[60px] border-2 border-[#E9ECF0] bg-white px-[26px] py-[48px] transition-colors duration-300 dark:border-[#2a2d33] dark:bg-[#232832] md:px-[32px]',
        compact ? 'max-w-[402px]' : 'max-w-[520px]'
      )}
    >
      {children}
    </div>
  );
}

export {AuthCountrySelect} from './auth-country-select';

/** Register step 2: date field with visible "Birth Date" placeholder. */
export function AuthBirthDateField({
  name,
  className = 'w-[163px] shrink-0',
  placeholder,
}: {
  name: string;
  className?: string;
  placeholder?: ReactNode;
}) {
  const {control} = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({field}) => {
        const isEmpty = !field.value;
        return (
          <div className={clsx('relative', className)}>
            <input
              {...field}
              type="date"
              value={field.value ?? ''}
              className={clsx(
                authFieldHalfClass,
                'w-full',
                isEmpty &&
                  '[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0'
              )}
            />
            {isEmpty ? (
              <span
                className="pointer-events-none absolute left-[20px] top-1/2 -translate-y-1/2 text-[16px] font-normal text-[#989898] dark:text-[#7d8794]"
                aria-hidden
              >
                {placeholder ?? <Trans message="Birth Date" />}
              </span>
            ) : null}
          </div>
        );
      }}
    />
  );
}

/** Step 2 register: confirmed email with green underline. */
export function AuthEmailDisplay({email}: {email: string}) {
  return (
    <div className="mb-[16px] flex w-[350px] max-w-full flex-col items-center">
      <p className="max-w-full truncate text-center text-[20px] font-normal leading-normal text-[#2B7E20]">
        {email}
      </p>
      <div className="mt-[8px] h-[2px] w-[225px] max-w-full shrink-0 bg-[#2B7E20]" />
    </div>
  );
}

export function AuthTitle({children}: {children: ReactNode}) {
  return (
    <h1 className="mb-[32px] w-full text-center text-[25px] font-normal leading-normal text-[#4C4C4C] dark:text-[#f3f4f6]">
      {children}
    </h1>
  );
}

interface AuthPromptCardProps {
  text: ReactNode;
  buttonLabel: string;
  buttonTo: string;
}

export function AuthPromptCard({
  text,
  buttonLabel,
  buttonTo,
}: AuthPromptCardProps) {
  return (
    <div className="main-content-2 mt-[30px] flex min-w-[300px] max-w-[402px] w-full flex-col items-center justify-center rounded-[60px] border-2 border-[#E9ECF0] bg-white px-[26px] py-[32px] text-center transition-colors duration-300 dark:border-[#2a2d33] dark:bg-[#232832]">
      <p className="mb-[20px] whitespace-nowrap text-[16px] font-normal leading-normal text-[#4C4C4C] dark:text-[#f3f4f6]">
        {text}
      </p>
      <Link to={buttonTo} className={authOutlineBtnClass}>
        {buttonLabel}
      </Link>
    </div>
  );
}

function AuthCloseIconSvg() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden
    >
      <circle cx="20" cy="20" r="20" fill="#D8562A" />
      <path
        d="M14.166 14.1665L25.8327 25.8332"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M14.166 25.8335L25.8327 14.1668"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Red close — just outside the form card top-right corner. */
export function AuthCloseButton({to = '/'}: {to?: string}) {
  return (
    <Link
      to={to}
      className="flex h-[40px] w-[40px] shrink-0 items-center justify-center transition-opacity hover:opacity-90"
      aria-label="Close"
    >
      <AuthCloseIconSvg />
    </Link>
  );
}

function AuthBackIconSvg() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden
    >
      <circle cx="20" cy="20" r="20" fill="#D8562A" />
      <path
        d="M11.4366 22.0912C10.0609 21.3301 10.0609 19.3523 11.4366 18.5912L24.1957 11.5322C25.5287 10.7947 27.1639 11.7588 27.1639 13.2822V27.4002C27.1639 28.9236 25.5287 29.8877 24.1957 29.1502L11.4366 22.0912Z"
        fill="white"
      />
    </svg>
  );
}

export function AuthBackButton({onClick}: {onClick: () => void}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-[40px] w-[40px] shrink-0 items-center justify-center transition-opacity hover:opacity-90"
      aria-label="Go back"
    >
      <AuthBackIconSvg />
    </button>
  );
}

export function AuthCheckbox({
  id,
  checked,
  onChange,
  hasError,
  align = 'start',
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hasError?: boolean;
  /** Login: remember-me row sits slightly inset / centered in the card. */
  align?: 'start' | 'center';
  children: ReactNode;
}) {
  const label = (
    <label
      htmlFor={id}
      className={clsx(
        'flex cursor-pointer items-center gap-[12px]',
        align === 'center' ? 'w-fit' : 'w-[350px] max-w-full',
        hasError && 'text-red-600'
      )}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="sr-only"
      />
      <span
        className={clsx(
          'flex h-[25px] w-[25px] shrink-0 items-center justify-center rounded-[9px] border-2 transition-colors',
          checked
            ? 'border-[#2B7E20] bg-[#2B7E20]'
            : 'border-[#E9ECF0] bg-[#E9ECF0] dark:border-[#2a2d33] dark:bg-[#1d2128]',
          hasError && !checked && 'border-red-500'
        )}
        aria-hidden
      >
        {checked ? (
          <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
            <path
              d="M1 5.5L5 9.5L13 1"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </span>
      <span className="whitespace-nowrap text-[16px] font-normal leading-normal text-[#4C4C4C] dark:text-[#b3bcc8]">
        {children}
      </span>
    </label>
  );

  if (align === 'center') {
    return (
      <div className="flex w-[350px] max-w-full justify-center">{label}</div>
    );
  }

  return label;
}
